import { TrendingUp, Star, CheckCircle2, XCircle, Award, Target, Zap, Trophy } from 'lucide-react';

interface PerformanceDashboardScreenProps {
  onBack: () => void;
}

export function PerformanceDashboardScreen({ onBack }: PerformanceDashboardScreenProps) {
  const stats = {
    totalDeliveries: 247,
    acceptanceRate: 94,
    completionRate: 98,
    averageRating: 4.8,
    totalEarnings: 124500,
    onTimeRate: 96,
  };

  const badges = [
    { id: 1, name: 'Speed Demon', icon: Zap, description: '100 deliveries in a week', earned: true },
    { id: 2, name: 'Perfect Week', icon: Star, description: '5-star rating all week', earned: true },
    { id: 3, name: 'Early Bird', icon: Trophy, description: 'Most morning deliveries', earned: false },
    { id: 4, name: 'Top Performer', icon: Award, description: 'Top 10% this month', earned: true },
  ];

  const weeklyStats = [
    { day: 'Mon', deliveries: 12, earnings: 6200 },
    { day: 'Tue', deliveries: 15, earnings: 7800 },
    { day: 'Wed', deliveries: 18, earnings: 9400 },
    { day: 'Thu', deliveries: 14, earnings: 7200 },
    { day: 'Fri', deliveries: 20, earnings: 10500 },
    { day: 'Sat', deliveries: 16, earnings: 8400 },
    { day: 'Sun', deliveries: 10, earnings: 5200 },
  ];

  const maxDeliveries = Math.max(...weeklyStats.map(s => s.deliveries));

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-br from-[#E0001B]/10 to-[#00B277]/10">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E0001B] to-[#00B277] flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-[28px] text-[#111111] mb-1">
              Performance
            </h1>
            <p className="text-[#6B6B6B]">Your stats and achievements</p>
          </div>
        </div>

        {/* Rating Card */}
        <div className="p-6 bg-white rounded-[16px] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[14px] text-[#6B6B6B] mb-1">Overall Rating</p>
              <div className="flex items-center gap-2">
                <span className="text-[32px] font-bold text-[#111111]">{stats.averageRating}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.floor(stats.averageRating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : star - 0.5 <= stats.averageRating
                          ? 'fill-yellow-400/50 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[14px] text-[#6B6B6B]">Based on</p>
              <p className="text-[20px] font-bold text-[#111111]">{stats.totalDeliveries} rides</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-green-50 rounded-[12px] border border-green-100">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-[14px] text-green-900">Acceptance Rate</span>
            </div>
            <p className="text-[28px] font-bold text-green-600">{stats.acceptanceRate}%</p>
          </div>

          <div className="p-4 bg-blue-50 rounded-[12px] border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="text-[14px] text-blue-900">Completion Rate</span>
            </div>
            <p className="text-[28px] font-bold text-blue-600">{stats.completionRate}%</p>
          </div>

          <div className="p-4 bg-purple-50 rounded-[12px] border border-purple-100">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span className="text-[14px] text-purple-900">On-Time Rate</span>
            </div>
            <p className="text-[28px] font-bold text-purple-600">{stats.onTimeRate}%</p>
          </div>

          <div className="p-4 bg-orange-50 rounded-[12px] border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5 text-orange-600" />
              <span className="text-[14px] text-orange-900">Total Earnings</span>
            </div>
            <p className="text-[24px] font-bold text-orange-600">₦{(stats.totalEarnings / 1000).toFixed(0)}k</p>
          </div>
        </div>

        {/* Weekly Performance Chart */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">This Week's Activity</h2>
          <div className="p-6 bg-gray-50 rounded-[12px]">
            <div className="flex items-end justify-between gap-2 h-40 mb-4">
              {weeklyStats.map(stat => (
                <div key={stat.day} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full bg-gradient-to-t from-[#E0001B] to-[#E0001B]/60 rounded-t-md transition-all hover:from-[#E0001B] hover:to-[#E0001B]"
                    style={{ height: `${(stat.deliveries / maxDeliveries) * 100}%` }}
                  />
                  <span className="text-[12px] text-[#6B6B6B] font-medium">{stat.day}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2 text-center">
              {weeklyStats.map(stat => (
                <div key={stat.day}>
                  <p className="text-[14px] font-bold text-[#111111]">{stat.deliveries}</p>
                  <p className="text-[10px] text-[#6B6B6B]">₦{(stat.earnings / 1000).toFixed(1)}k</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Achievements & Badges */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Achievements</h2>
          <div className="grid grid-cols-2 gap-3">
            {badges.map(badge => {
              const Icon = badge.icon;
              return (
                <div
                  key={badge.id}
                  className={`p-4 rounded-[12px] border-2 ${
                    badge.earned
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                    badge.earned ? 'bg-yellow-400' : 'bg-gray-300'
                  }`}>
                    <Icon className={`w-6 h-6 ${badge.earned ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <h3 className="text-[14px] font-medium mb-1">{badge.name}</h3>
                  <p className="text-[12px] text-[#6B6B6B]">{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Performance Tips */}
        <div className="p-6 bg-blue-50 rounded-[12px] border border-blue-100">
          <h3 className="text-[16px] font-medium text-blue-900 mb-3">💡 Tips to Improve</h3>
          <ul className="space-y-2 text-[14px] text-blue-800">
            <li>• Accept more jobs during peak hours for higher earnings</li>
            <li>• Maintain 95%+ acceptance rate for bonus eligibility</li>
            <li>• Communicate proactively with customers for better ratings</li>
            <li>• Complete deliveries 5 minutes early for on-time bonuses</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
